import { useEffect } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { useAuthStore } from './store';
import { useCurrentUser } from './queries';

/**
 * ProtectedRoute — gates child routes behind a successful /users/me.
 *
 * Behaviour:
 *   - During bootstrap (initial /me call), renders a tiny loading shell.
 *   - On 401 / null user, redirects to /login and remembers the intended
 *     destination via location state, so post-login lands on the right page.
 *   - On success, renders <Outlet/> for the matched child route.
 *
 * Role-based gating happens at a finer level (per-page guards in F2+);
 * this only enforces "is logged in".
 */
export function ProtectedRoute() {
  const location = useLocation();
  const user = useAuthStore((s) => s.user);
  const bootstrapped = useAuthStore((s) => s.bootstrapped);
  const { isLoading, isError } = useCurrentUser();

  useEffect(() => {
    // No-op effect kept to make refactor-time intent obvious; future
    // F2 role gating can hook here.
  }, [user]);

  if (!bootstrapped && isLoading) {
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background text-muted-foreground"
        role="status"
        aria-busy="true"
      >
        <span className="text-sm">…</span>
      </div>
    );
  }
  if (isError || !user) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }
  return <Outlet />;
}
